/**
 * Maintenance Jobs View (Admin)
 * Admin-facing view for all maintenance jobs across all clients
 * 
 * Features:
 * - Dashboard metrics
 * - Calendar view
 * - Table view with all jobs
 * - Filter by client, status, date range
 * - Actions: View details, Notify client, Mark complete, Cancel
 */

import React, { useContext, useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Grid
} from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';
import {
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Email as NotifyIcon,
  CheckCircle as CompleteIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  ViewList as TableIcon
} from '@material-ui/icons';
import { MaintenanceContext } from '../contexts/MaintenanceContext';
import MaintenanceDashboard from '../components/MaintenanceDashboard';
import MaintenanceCalendar from '../components/MaintenanceCalendar';
import JobStatusBadge from '../components/JobStatusBadge';
import NotificationBadges from '../components/NotificationBadges';
import ConfirmDialog from '../components/ConfirmDialog';
import moment from 'moment';

const MaintenanceJobs = () => {
  const {
    jobs,
    isLoading,
    loadJobs,
    loadJobDetails,
    notifyClient,
    completeJob,
    cancelJob,
    selectedJob,
    setSelectedJob
  } = useContext(MaintenanceContext);

  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [currentTab, setCurrentTab] = useState(0); // 0 = Calendar, 1 = Table
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState(null);
  
  // Form fields
  const [completionNotes, setCompletionNotes] = useState('');
  const [actualDowntime, setActualDowntime] = useState('');

  // Get unique clients for filter
  const clients = [...new Set(jobs.map(j => j.client_name))].sort();

  // Load jobs on mount and when filters change
  useEffect(() => {
    const filters = {};
    if (statusFilter !== 'all') filters.status = statusFilter;
    if (clientFilter !== 'all') filters.client_name = clientFilter;
    loadJobs(filters);
  }, [statusFilter, clientFilter]);

  // Handle dashboard card clicks
  const handleDashboardFilter = (filter) => {
    if (filter.status) {
      setStatusFilter(filter.status);
    } else if (filter.dateRange === 'week') {
      setStatusFilter('all');
      setCurrentTab(0); // Switch to calendar
    } else if (filter.dateRange === 'month' && filter.status === 'completed') {
      setStatusFilter('completed');
    } else if (filter.pendingNotifications) {
      setStatusFilter('pending');
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Handle view details
  const handleViewDetails = async (jobId) => {
    await loadJobDetails(jobId);
    setDetailsDialogOpen(true);
  };

  // Handle notify client
  const handleNotifyClient = async (jobId) => {
    const success = await notifyClient(jobId);
    if (success) {
      loadJobs();
    }
  };

  // Handle complete job
  const handleCompleteJobDialog = (jobId) => {
    setSelectedJobId(jobId);
    setCompletionNotes('');
    setActualDowntime('');
    setCompleteDialogOpen(true);
  };

  const handleCompleteJobSubmit = async () => {
    const success = await completeJob(selectedJobId, {
      completion_notes: completionNotes,
      actual_downtime_minutes: actualDowntime ? parseInt(actualDowntime) : null
    });
    
    if (success) {
      setCompleteDialogOpen(false);
      loadJobs();
    }
  };

  // Handle cancel job - open confirmation
  const handleCancelJob = (jobId) => {
    setJobToCancel(jobId);
    setCancelConfirmOpen(true);
  };

  // Handle cancel job - confirmed
  const handleCancelJobConfirmed = async () => {
    const success = await cancelJob(jobToCancel);
    if (success) {
      setCancelConfirmOpen(false);
      setJobToCancel(null);
      loadJobs();
    }
  };

  // Handle cancel job - cancelled
  const handleCancelJobCancelled = () => {
    setCancelConfirmOpen(false);
    setJobToCancel(null);
  };

  // DataGrid columns
  const columns = [
    {
      field: 'scheduled_date',
      headerName: 'Date',
      width: 120,
      valueFormatter: (params) => moment(params.value).format('MMM DD, YYYY')
    },
    {
      field: 'scheduled_time',
      headerName: 'Time',
      width: 80,
      valueFormatter: (params) => params.value || '09:00'
    },
    {
      field: 'client_name',
      headerName: 'Client',
      width: 180
    },
    {
      field: 'schedule_title',
      headerName: 'Title',
      width: 200,
      flex: 1
    },
    {
      field: 'assigned_engineer_name',
      headerName: 'Engineer',
      width: 150
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 220,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.75} style={{ padding: '4px 0' }}>
          <JobStatusBadge
            status={params.value}
            clientNotified={params.row.client_notified_at}
            engineerReminded={params.row.engineer_reminded_at}
            size="small"
          />
          <NotificationBadges
            clientNotified={params.row.client_notified_at}
            engineerReminded={params.row.engineer_reminded_at}
            notificationDaysBefore={params.row.notification_days_before}
            scheduledDate={params.row.scheduled_date}
          />
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={() => handleViewDetails(params.row.id)}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          
          {!params.row.client_notified_at && params.row.status !== 'completed' && params.row.status !== 'cancelled' && (
            <Tooltip title="Notify Client">
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleNotifyClient(params.row.id)}
              >
                <NotifyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {(params.row.status === 'pending' || params.row.status === 'in_progress' || params.row.status === 'overdue') && (
            <>
              <Tooltip title="Mark Complete">
                <IconButton
                  size="small"
                  style={{ color: '#4caf50' }}
                  onClick={() => handleCompleteJobDialog(params.row.id)}
                >
                  <CompleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Cancel Job">
                <IconButton
                  size="small"
                  color="secondary"
                  onClick={() => handleCancelJob(params.row.id)}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )
    }
  ];

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Maintenance Jobs
        </Typography>
        
        <Box display="flex" alignItems="center" gap={2}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Client</InputLabel>
            <Select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              label="Client"
            >
              <MenuItem value="all">All Clients</MenuItem>
              {clients.map(client => (
                <MenuItem key={client} value={client}>{client}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          
          <Tooltip title="Refresh">
            <IconButton
              onClick={() => loadJobs()}
              disabled={isLoading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Dashboard Metrics */}
      <MaintenanceDashboard 
        jobs={jobs} 
        onFilterClick={handleDashboardFilter}
        isLoading={isLoading}
      />

      {/* Tabs */}
      <Paper>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab icon={<CalendarIcon />} label="Calendar" />
          <Tab icon={<TableIcon />} label="Table" />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box mt={2}>
        {currentTab === 0 && (
          <MaintenanceCalendar
            jobs={jobs}
            onNotifyClient={handleNotifyClient}
            onCompleteJob={handleCompleteJobDialog}
            onCancelJob={handleCancelJob}
          />
        )}

        {currentTab === 1 && (
          <Paper style={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={jobs}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50]}
              loading={isLoading}
              disableSelectionOnClick
              density="comfortable"
            />
          </Paper>
        )}
      </Box>

      {/* Job Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedJob?.job?.schedule_title || 'Job Details'}
            </Typography>
            {selectedJob?.job && (
              <JobStatusBadge
                status={selectedJob.job.status}
                clientNotified={selectedJob.job.client_notified_at}
                engineerReminded={selectedJob.job.engineer_reminded_at}
                size="medium"
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : selectedJob?.job ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">CLIENT</Typography>
                <Typography variant="body1">{selectedJob.job.client_name}</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">SCHEDULED DATE</Typography>
                <Typography variant="body1">
                  {moment(selectedJob.job.scheduled_date).format('MMMM DD, YYYY')} at {selectedJob.job.scheduled_time || '09:00'}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">ASSIGNED ENGINEER</Typography>
                <Typography variant="body1">{selectedJob.job.assigned_engineer_name || 'Unassigned'}</Typography>
              </Grid>

              {selectedJob.job.asset_name && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">ASSET</Typography>
                  <Typography variant="body1">
                    {selectedJob.job.asset_name} ({selectedJob.job.asset_type || 'N/A'})
                  </Typography>
                </Grid>
              )}

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">EXPECTED DOWNTIME</Typography>
                <Typography variant="body1">{selectedJob.job.expected_downtime_minutes || 30} minutes</Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">NOTIFICATIONS</Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <NotificationBadges
                    clientNotified={selectedJob.job.client_notified_at}
                    engineerReminded={selectedJob.job.engineer_reminded_at}
                    notificationDaysBefore={selectedJob.job.notification_days_before}
                    scheduledDate={selectedJob.job.scheduled_date}
                  />
                </Box>
              </Grid>

              {selectedJob.job.instructions && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">INSTRUCTIONS</Typography>
                  <Paper style={{ padding: 12, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="body2">{selectedJob.job.instructions}</Typography>
                  </Paper>
                </Grid>
              )}

              {selectedJob.job.completion_notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">COMPLETION NOTES</Typography>
                  <Paper style={{ padding: 12, backgroundColor: '#f5f5f5' }}>
                    <Typography variant="body2">{selectedJob.job.completion_notes}</Typography>
                  </Paper>
                </Grid>
              )}

              {selectedJob.job.actual_downtime_minutes && (
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">ACTUAL DOWNTIME</Typography>
                  <Typography variant="body1">{selectedJob.job.actual_downtime_minutes} minutes</Typography>
                </Grid>
              )}

              {selectedJob.job.completed_at && (
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="textSecondary">COMPLETED</Typography>
                  <Typography variant="body1">
                    {moment(selectedJob.job.completed_at).format('MMM DD, YYYY h:mm A')}
                  </Typography>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography>No details available</Typography>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>
            Close
          </Button>
          {selectedJob?.job && !selectedJob.job.client_notified_at && selectedJob.job.status !== 'completed' && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                handleNotifyClient(selectedJob.job.id);
                setDetailsDialogOpen(false);
              }}
              startIcon={<NotifyIcon />}
            >
              Notify Client
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Complete Job Dialog */}
      <Dialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark Job Complete</DialogTitle>
        
        <DialogContent>
          <TextField
            label="Completion Notes *"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={completionNotes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            margin="normal"
            placeholder="Describe what was done, any issues encountered, etc."
            required
          />
          
          <TextField
            label="Actual Downtime (minutes)"
            type="number"
            fullWidth
            variant="outlined"
            value={actualDowntime}
            onChange={(e) => setActualDowntime(e.target.value)}
            margin="normal"
            placeholder="Optional: How many minutes of downtime occurred?"
          />
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>
            Close
          </Button>
          <Button
            onClick={handleCompleteJobSubmit}
            color="primary"
            variant="contained"
            disabled={isLoading || !completionNotes.trim()}
          >
            Mark Complete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Job Confirmation Dialog */}
      <ConfirmDialog
        open={cancelConfirmOpen}
        title="Cancel Maintenance Job"
        message="Are you sure you want to cancel this maintenance job? This action cannot be undone."
        onConfirm={handleCancelJobConfirmed}
        onCancel={handleCancelJobCancelled}
        confirmText="Cancel Job"
        cancelText="Go Back"
        confirmColor="secondary"
      />
    </div>
  );
};

export default MaintenanceJobs;
